/**
 * shared/pmove/slide.ts — PM_ClipVelocity, PM_ProjectVelocity, PM_SlideMove, PM_StepSlideMove.
 *
 * OWNER: PMOVE agent.
 *
 * T6 replaced the slide loop with a contact-constraint solver (0x6feaa0, [PM §3 PM_SlideMove]); the
 * port note [PM §3, §8.10] says a Q3-style 4-bump slide with ClipVelocity overbounce 1.001 and
 * trapezoid gravity matches it on axis-aligned/simple geometry. We port the IW-lineage version of
 * that loop exactly as T5 MP had it before the solver ([T5 bg_slidemove.cpp:PM_SlideMove], the
 * non-gjk branch: 4 bumps, up to 8 planes, restrictive-plane permutation), plus T5's
 * PM_StepSlideMove, whose constants all match T6 [PM §3 PM_StepSlideMove]:
 * step 18 (prone 10), snap-down 9, step-up retried on ground normal.z < 0.9, step speed keep
 * (1-|dz|/step)*0.8+0.2, bob advance min(|dz|/2,4)*1.25+7.
 */
import { createTraceResult } from '../collision/types';
import { ENTITYNUM_NONE, PM_STEP_DOWN_SNAP, PM_STEP_SIZE, PM_STEP_SIZE_PRONE, PM_STEP_SLOPE_NORMAL_Z } from '../constants';
import { vnormalize, type ReadonlyVec3, type Vec3 } from '../math';
import { PEv, PMF, PmType } from '../types';
import { footstepEvent } from './footsteps';
import { jumpClampVelocity, jumpClearState, jumpGetStepHeight, jumpIsPlayerAboveMax } from './jump';
import { PM_FOOTSTEPS_THRESHOLD, addTouchEnt, pmTrace, traceHitClient, traceWalkable, type Pm } from './pm';
import { verifyPronePosition } from './stance';

/** EQUAL_EPSILON 0.001: IW form of Q3's OVERCLIP 1.001 [MOVPUB §1.4; PM §3 AirMove "0.001 @0xc44e34"]. */
const EQUAL_EPSILON = 0.001;

/** PM_ClipVelocity [T5 bg_pmove.cpp:365]: out = in - n * (dot - |dot| * 0.001). out may alias in. */
export function clipVelocity(inp: ReadonlyVec3, normal: ReadonlyVec3, out: Vec3): void {
  let parallel = inp[0] * normal[0] + inp[1] * normal[1] + inp[2] * normal[2];
  parallel = parallel - Math.abs(parallel) * EQUAL_EPSILON;
  const s = -parallel;
  out[0] = inp[0] + s * normal[0];
  out[1] = inp[1] + s * normal[1];
  out[2] = inp[2] + s * normal[2];
}

/**
 * PM_ProjectVelocity (0x5d8c60) [PM §3]: keep the horizontal velocity, recompute z so the motion
 * lies on the plane, then rescale to the old length when going down / rising / not longer.
 * NOTE: when none of the three conditions holds the output is left untouched (as in the code).
 */
export function projectVelocity(velIn: ReadonlyVec3, normal: ReadonlyVec3, velOut: Vec3): void {
  const lengthSq2D = velIn[0] * velIn[0] + velIn[1] * velIn[1];
  if (Math.abs(normal[2]) < EQUAL_EPSILON || lengthSq2D === 0) {
    velOut[0] = velIn[0]; velOut[1] = velIn[1]; velOut[2] = velIn[2];
    return;
  }
  const newZ = -(normal[0] * velIn[0] + normal[1] * velIn[1]) / normal[2];
  const originalLengthSq = velIn[2] * velIn[2] + lengthSq2D;
  const adjustedLengthSq = newZ * newZ + lengthSq2D;
  const lengthScale = Math.sqrt(originalLengthSq / adjustedLengthSq);
  if (lengthScale < 1 || newZ < 0 || velIn[2] > 0) {
    const x = velIn[0], y = velIn[1];
    velOut[0] = x * lengthScale;
    velOut[1] = y * lengthScale;
    velOut[2] = newZ * lengthScale;
  }
}

// ---- scratch -------------------------------------------------------------------------------
const MAX_CLIP_PLANES = 8;
const planes: Vec3[] = Array.from({ length: MAX_CLIP_PLANES }, () => [0, 0, 0] as Vec3);
const permutation: number[] = new Array<number>(MAX_CLIP_PLANES).fill(0);
const parallelTmp: number[] = new Array<number>(MAX_CLIP_PLANES).fill(0);
const primalVelocity: Vec3 = [0, 0, 0];
const endVelocity: Vec3 = [0, 0, 0];
const clipVel: Vec3 = [0, 0, 0];
const endClipVel: Vec3 = [0, 0, 0];
const dir: Vec3 = [0, 0, 0];
const end: Vec3 = [0, 0, 0];
const tr = createTraceResult();

/** PM_PermuteRestrictiveClipPlanes [T5 bg_slidemove.cpp:2371]: insertion sort by dot(v, plane). */
function permuteRestrictiveClipPlanes(velocity: ReadonlyVec3, planeCount: number): number {
  for (let planeIndex = 0; planeIndex < planeCount; planeIndex++) {
    const p = planes[planeIndex];
    parallelTmp[planeIndex] = velocity[0] * p[0] + velocity[1] * p[1] + velocity[2] * p[2];
    let permutedIndex = planeIndex;
    for (; permutedIndex && parallelTmp[planeIndex] <= parallelTmp[permutation[permutedIndex - 1]]; --permutedIndex) {
      permutation[permutedIndex] = permutation[permutedIndex - 1];
    }
    permutation[permutedIndex] = planeIndex;
  }
  return parallelTmp[permutation[0]];
}

function dot3(a: ReadonlyVec3, b: ReadonlyVec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * PM_SlideMove [T5 bg_slidemove.cpp:2773, non-gjk branch]. Returns true if anything was hit.
 * Gravity is integrated with the trapezoid rule [PM §3 step 1]: the move uses (v0+v1)/2 and the
 * final velocity is v1 (clipped like the move velocity).
 *
 * DEVIATION: T5/Q3 restore the un-clipped primal velocity while ps.pm_time is running
 * ("if (ps->pm_time) velocity = primal_velocity"). T6's solver returns the solver velocity
 * [PM §3 "Velocity at the end = the solver's velocity"], so that quirk is not ported.
 */
export function slideMove(pm: Pm, gravity: boolean): boolean {
  const ps = pm.ps;
  const numbumps = 4;
  primalVelocity[0] = ps.velocity[0]; primalVelocity[1] = ps.velocity[1]; primalVelocity[2] = ps.velocity[2];
  endVelocity[0] = ps.velocity[0]; endVelocity[1] = ps.velocity[1]; endVelocity[2] = ps.velocity[2];
  if (gravity) {
    endVelocity[2] = endVelocity[2] - ps.gravity * pm.frametime;
    ps.velocity[2] = (ps.velocity[2] + endVelocity[2]) * 0.5;
    primalVelocity[2] = endVelocity[2];
    if (pm.groundPlane) clipVelocity(ps.velocity, pm.groundTrace.normal, ps.velocity);
  }
  let timeLeft = pm.frametime;
  let numplanes = 0;
  if (pm.groundPlane) {
    planes[0][0] = pm.groundTrace.normal[0]; planes[0][1] = pm.groundTrace.normal[1]; planes[0][2] = pm.groundTrace.normal[2];
    numplanes = 1;
  }
  vnormalize(planes[numplanes], ps.velocity);
  numplanes++;
  let bumpcount = 0;
  for (; bumpcount < numbumps; bumpcount++) {
    end[0] = ps.origin[0] + timeLeft * ps.velocity[0];
    end[1] = ps.origin[1] + timeLeft * ps.velocity[1];
    end[2] = ps.origin[2] + timeLeft * ps.velocity[2];
    pmTrace(pm, tr, ps.origin, pm.mins, pm.maxs, end);
    if (tr.allsolid) {
      ps.velocity[2] = 0; // entity is completely trapped in another solid
      return true;
    }
    if (tr.fraction > 0) {
      ps.origin[0] = tr.endpos[0]; ps.origin[1] = tr.endpos[1]; ps.origin[2] = tr.endpos[2];
    }
    if (tr.fraction === 1) break;
    addTouchEnt(pm, tr.entityNum);
    timeLeft -= timeLeft * tr.fraction;
    if (numplanes >= MAX_CLIP_PLANES) {
      ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
      return true;
    }
    // same plane hit twice: nudge the velocity out along the normal (avoids epsilon issues)
    let i = 0;
    for (; i < numplanes; i++) {
      if (dot3(tr.normal, planes[i]) > 0.999) {
        clipVelocity(ps.velocity, tr.normal, ps.velocity);
        ps.velocity[0] += tr.normal[0]; ps.velocity[1] += tr.normal[1]; ps.velocity[2] += tr.normal[2];
        break;
      }
    }
    if (i < numplanes) continue;
    const np = planes[numplanes];
    np[0] = tr.normal[0]; np[1] = tr.normal[1]; np[2] = tr.normal[2];
    numplanes++;
    const into = permuteRestrictiveClipPlanes(ps.velocity, numplanes);
    if (into < 0.1) {
      if (-into > pm.impactSpeed) pm.impactSpeed = -into;
      const p0 = planes[permutation[0]];
      clipVelocity(ps.velocity, p0, clipVel);
      clipVelocity(endVelocity, p0, endClipVel);
      for (let j = 1; j < numplanes; j++) {
        const pj = planes[permutation[j]];
        if (dot3(clipVel, pj) >= 0.1) continue;
        clipVelocity(clipVel, pj, clipVel);
        clipVelocity(endClipVel, pj, endClipVel);
        if (dot3(clipVel, p0) >= 0) continue;
        // slide along the crease of the two planes
        dir[0] = p0[1] * pj[2] - p0[2] * pj[1];
        dir[1] = p0[2] * pj[0] - p0[0] * pj[2];
        dir[2] = p0[0] * pj[1] - p0[1] * pj[0];
        vnormalize(dir);
        let d = dot3(dir, ps.velocity);
        clipVel[0] = d * dir[0]; clipVel[1] = d * dir[1]; clipVel[2] = d * dir[2];
        d = dot3(dir, endVelocity);
        endClipVel[0] = d * dir[0]; endClipVel[1] = d * dir[1]; endClipVel[2] = d * dir[2];
        for (let k = 1; k < numplanes; k++) {
          if (k === j) continue;
          if (dot3(clipVel, planes[permutation[k]]) < 0.1) {
            ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0; // stop dead at a triple-plane corner
            return true;
          }
        }
      }
      ps.velocity[0] = clipVel[0]; ps.velocity[1] = clipVel[1]; ps.velocity[2] = clipVel[2];
      endVelocity[0] = endClipVel[0]; endVelocity[1] = endClipVel[1]; endVelocity[2] = endClipVel[2];
    }
  }
  if (gravity) {
    ps.velocity[0] = endVelocity[0]; ps.velocity[1] = endVelocity[1]; ps.velocity[2] = endVelocity[2];
  }
  return bumpcount !== 0;
}

// ---- StepSlideMove scratch -----------------------------------------------------------------
const startO: Vec3 = [0, 0, 0];
const startV: Vec3 = [0, 0, 0];
const downO: Vec3 = [0, 0, 0];
const downV: Vec3 = [0, 0, 0];
const up: Vec3 = [0, 0, 0];
const down: Vec3 = [0, 0, 0];
const stepTr = createTraceResult();
const stepSizeOut = { value: 0 };

/** Result of the last stepSlideMove (for tests / debugging). */
export const lastStep = { dz: 0 };

/**
 * PM_StepSlideMove [T5 bg_slidemove.cpp:2420; T6 0x442090, PM §3].
 */
export function stepSlideMove(pm: Pm, gravity: boolean): void {
  const ps = pm.ps;
  let fStepAmount = 0;
  let jumping = false;
  let bHadGround: boolean;
  lastStep.dz = 0;
  if (ps.pm_flags & PMF.LADDER) {
    bHadGround = false;
    jumpClearState(ps);
  } else if (pm.groundPlane) {
    bHadGround = true;
  } else {
    bHadGround = false;
    if ((ps.pm_flags & PMF.JUMPING) && ps.pm_time) jumpClearState(ps);
  }
  startO[0] = ps.origin[0]; startO[1] = ps.origin[1]; startO[2] = ps.origin[2];
  startV[0] = ps.velocity[0]; startV[1] = ps.velocity[1]; startV[2] = ps.velocity[2];
  const iBumps = slideMove(pm, gravity);
  // step 18, prone 10 (@0xc48898 / @0xc6c564)
  let fStepSize = ps.pm_flags & PMF.PRONE ? PM_STEP_SIZE_PRONE : PM_STEP_SIZE;
  if (ps.groundEntityNum === ENTITYNUM_NONE) {
    if ((ps.pm_flags & PMF.JUMPING) && ps.pm_time) jumpClearState(ps);
    // PM_CheckJumpStep (0x4ff870): near the top of a jump you may step up onto a ledge
    if (iBumps && (ps.pm_flags & PMF.JUMPING) && jumpGetStepHeight(pm, startO, stepSizeOut)) {
      fStepSize = stepSizeOut.value;
      if (fStepSize < 1) return;
      jumping = true;
    }
    if (!jumping && !((ps.pm_flags & PMF.LADDER) && ps.velocity[2] > 0)) return;
  }
  downO[0] = ps.origin[0]; downO[1] = ps.origin[1]; downO[2] = ps.origin[2];
  downV[0] = ps.velocity[0]; downV[1] = ps.velocity[1]; downV[2] = ps.velocity[2];
  const flatDelta0 = downO[0] - startO[0];
  const flatDelta1 = downO[1] - startO[1];
  // step up: when blocked, or on a ground plane steeper than normal.z 0.9 (@0xc21c3c)
  if (iBumps || (pm.groundPlane && pm.groundTrace.normal[2] < PM_STEP_SLOPE_NORMAL_Z)) {
    up[0] = startO[0]; up[1] = startO[1]; up[2] = startO[2] + fStepSize + 1;
    pmTrace(pm, stepTr, startO, pm.mins, pm.maxs, up);
    fStepAmount = (fStepSize + 1) * stepTr.fraction - 1;
    if (fStepAmount >= 1) {
      ps.origin[0] = up[0]; ps.origin[1] = up[1]; ps.origin[2] = startO[2] + fStepAmount;
      ps.velocity[0] = startV[0]; ps.velocity[1] = startV[1]; ps.velocity[2] = startV[2];
      slideMove(pm, gravity);
    } else {
      fStepAmount = 0;
    }
  }
  if (bHadGround || fStepAmount !== 0) {
    down[0] = ps.origin[0]; down[1] = ps.origin[1]; down[2] = ps.origin[2] - fStepAmount;
    if (bHadGround) down[2] -= PM_STEP_DOWN_SNAP; // 9 @0xc27bb4: stay glued to stairs/slopes
    pmTrace(pm, stepTr, ps.origin, pm.mins, pm.maxs, down);
    if (traceHitClient(stepTr)) {
      // never step onto another player: keep the plain slide result
      ps.origin[0] = downO[0]; ps.origin[1] = downO[1]; ps.origin[2] = downO[2];
      ps.velocity[0] = downV[0]; ps.velocity[1] = downV[1]; ps.velocity[2] = downV[2];
      return;
    }
    if (stepTr.fraction >= 1) {
      if (fStepAmount !== 0) ps.origin[2] -= fStepAmount;
    } else {
      // step landing reject: non-walkable and normal.z < 0.3 [MOVPUB §1.4; T5 bg_slidemove.cpp:2594]
      if (!traceWalkable(pm, stepTr) && stepTr.normal[2] < 0.3) {
        ps.origin[0] = downO[0]; ps.origin[1] = downO[1]; ps.origin[2] = downO[2];
        ps.velocity[0] = downV[0]; ps.velocity[1] = downV[1]; ps.velocity[2] = downV[2];
        return;
      }
      ps.origin[0] = stepTr.endpos[0]; ps.origin[1] = stepTr.endpos[1]; ps.origin[2] = stepTr.endpos[2];
      projectVelocity(ps.velocity, stepTr.normal, ps.velocity);
    }
  }
  // stepping must not lose horizontal progress (else use the no-step result, snapped down 9)
  const noStep = flatDelta0 * startV[0] + flatDelta1 * startV[1] + 0.001;
  const withStep = (ps.origin[0] - startO[0]) * startV[0] + (ps.origin[1] - startO[1]) * startV[1];
  if (noStep >= withStep || (jumping && jumpIsPlayerAboveMax(pm))) {
    ps.origin[0] = downO[0]; ps.origin[1] = downO[1]; ps.origin[2] = downO[2];
    ps.velocity[0] = downV[0]; ps.velocity[1] = downV[1]; ps.velocity[2] = downV[2];
    fStepAmount = 0;
    if (bHadGround) {
      down[0] = ps.origin[0]; down[1] = ps.origin[1]; down[2] = ps.origin[2] - PM_STEP_DOWN_SNAP;
      pmTrace(pm, stepTr, ps.origin, pm.mins, pm.maxs, down);
      if (stepTr.fraction < 1) {
        ps.origin[0] = stepTr.endpos[0]; ps.origin[1] = stepTr.endpos[1]; ps.origin[2] = stepTr.endpos[2];
        clipVelocity(ps.velocity, stepTr.normal, ps.velocity);
      }
    }
  }
  if (jumping) jumpClampVelocity(pm, downO);
  if (!bHadGround || ps.pm_type >= PmType.FREEZE) return;
  if (!verifyPronePosition(pm, startO, startV)) return;
  const dzStep = ps.origin[2] - downO[2];
  if (Math.abs(dzStep) <= 0.5) return;
  const iDelta = Math.trunc(dzStep + 9.313225746154785e-10);
  if (!iDelta) return;
  lastStep.dz = dzStep;
  // step feedback: view smoothing event (parm = |dz|, 0x8000 = stepping down) and speed loss
  pm.ctx.addEvent(ps, PEv.STEP, (Math.min(0x7fff, Math.round(Math.abs(dzStep))) | (dzStep < 0 ? 0x8000 : 0)));
  const fSpeedScale = (1 - 0.8) + (1 - Math.abs(ps.origin[2] - startO[2]) / fStepSize) * 0.8; // 0.8 @0xc2f67c, 0.2 @0xc3692c
  ps.velocity[0] *= fSpeedScale; ps.velocity[1] *= fSpeedScale; ps.velocity[2] *= fSpeedScale;
  pm.xyspeed = Math.sqrt(ps.velocity[0] * ps.velocity[0] + ps.velocity[1] * ps.velocity[1]);
  const absDelta = Math.abs(iDelta);
  if (absDelta > 3 && ps.groundEntityNum !== ENTITYNUM_NONE && pm.xyspeed >= PM_FOOTSTEPS_THRESHOLD) { // PM_ShouldMakeFootsteps
    const half = Math.min(4, Math.trunc(absDelta / 2));
    const old = ps.bobCycle;
    ps.bobCycle = Math.trunc(old + (half * 1.25 + 7)) & 0xff; // 1.25 @0xc40250, 7 @0xc2a108
    footstepEvent(pm, old, ps.bobCycle, true);
  }
}
